// src/components/integrations/synapse/SynapseFastqViewer.tsx
import React, { forwardRef, useEffect, useState } from 'react';

import { DocumentTextIcon } from '@heroicons/react/outline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { CircularProgress, Tooltip } from '@mui/material';

import AlertWrapper from '@components/AlertWrapper';
import Button from '@components/Button';
import ShadowCard from '@components/elements/ShadowCard';

import useSynapseFastqSearch from '@hooks/integrations/synapse/useSynapseFastqSearch';
import useSynapseFileSelection from '@hooks/integrations/synapse/useSynapseFileSelection';
import useSynapseImport from '@hooks/integrations/synapse/useSynapseImport';
import useSynapseUserProjects from '@hooks/integrations/synapse/useSynapseUserProjects';
import useSynapseWorkflows from '@hooks/integrations/synapse/useSynapseWorkflows';
import { useWebSocketEvent } from '@hooks/useWebSocketEvent';

import ArrowTopRightOnSquareIcon from '@icons/custom/ArrowTopRightOnSquareIcon';

import Experiment from '@models/Experiment';
import { SynapseFile } from '@models/integrations/synapse/SynapseFile';

import Logger from '@util/Logger';

import { getDisplayStatus } from './SynapseViewerUtils';

// Create a logger for WebSocket events at module level
const webSocketLogger = Logger.make('SynapseFastqViewer');

interface SynapseFastqViewerProps {
    modalView?: boolean;
    onFilesSelected?: (files: SynapseFile[]) => void;
    experiment?: Experiment;
    onDoneUploading?: () => void;
}

// Define the ref type as Record<string, never> to indicate an intentionally empty object
export type SynapseFastqViewerRef = Record<string, never>;

interface ProjectInfo {
    id: string;
    name: string;
    description?: string;
    createdOn?: string;
    modifiedOn?: string;
    createdBy?: string;
}

// Component for the Recent Projects dropdown
interface RecentProjectsDropdownProps {
    onSelectProject: (projectId: string) => void;
}

const RecentProjectsDropdown: React.FC<RecentProjectsDropdownProps> = ({ onSelectProject }) => {
    const { projects, loading, error } = useSynapseUserProjects();

    if (loading) {
        return (
            <div className="p-2 border rounded-md flex items-center">
                <CircularProgress size={16} className="mr-2" />
                <span className="text-sm">Loading recent projects...</span>
            </div>
        );
    }

    if (error) {
        return <div className="p-2 border rounded-md text-red-500 text-sm">Error loading projects: {error}</div>;
    }

    if (projects.length === 0) {
        return <div className="p-2 border rounded-md text-gray-500 text-sm">No recent projects found</div>;
    }

    return (
        <select
            id="recentProjects"
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            onChange={(e) => onSelectProject(e.target.value)}
            defaultValue=""
        >
            <option value="" disabled>
                Select a recent project
            </option>
            {projects.map((project) => (
                <option key={project.id} value={project.id}>
                    {project.name} ({project.id})
                </option>
            ))}
        </select>
    );
};

const SynapseFastqViewer = forwardRef<SynapseFastqViewerRef, SynapseFastqViewerProps>(
    ({ modalView = false, experiment, onDoneUploading }) => {
        const [projectId, setProjectId] = useState<string>('');
        const [isImporting, setIsImporting] = useState<boolean>(false);
        const [importError, setImportError] = useState<string | null>(null);
        const [importSuccess, setImportSuccess] = useState<string | null>(null);
        const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
        const [isLoadingProjectInfo, setIsLoadingProjectInfo] = useState<boolean>(false);

        // Use our custom hooks
        const { getProjectInfo, importFastqFiles } = useSynapseImport();

        const { fastqFiles, isSearching, searchError, progress, searchComplete, searchForFastqFiles } =
            useSynapseFastqSearch(projectId || null);

        const { selectedFiles, toggleFileSelection, toggleSelectAll, clearSelection } = useSynapseFileSelection();

        // Initialize the workflows hook
        const { existingWorkflows, isLoadingWorkflows, refreshExistingWorkflows, checkWorkflowStatus } =
            useSynapseWorkflows({
                experimentId: experiment?.uuid,
            });

        // Fetch initial workflow status when the component mounts or experimentId changes
        useEffect(() => {
            if (experiment?.uuid) {
                refreshExistingWorkflows();
            }
        }, [experiment?.uuid]);

        const handleProjectIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setProjectId(e.target.value);
        };

        useEffect(() => {
            if (projectId) {
                (async () => {
                    setIsLoadingProjectInfo(true);
                    try {
                        const info = await getProjectInfo(projectId);
                        setProjectInfo(info);
                    } catch (error) {
                        console.error('Error fetching project info:', error);
                    } finally {
                        setIsLoadingProjectInfo(false);
                    }
                    searchForFastqFiles();
                })();
            }
        }, [projectId]);

        const handleSearch = async (e: React.FormEvent) => {
            e.preventDefault();
            if (projectId) {
                // Fetch project info
                setIsLoadingProjectInfo(true);
                try {
                    const info = await getProjectInfo(projectId);
                    setProjectInfo(info);
                } catch (error) {
                    console.error('Error fetching project info:', error);
                } finally {
                    setIsLoadingProjectInfo(false);
                }

                // Search for FASTQ files
                searchForFastqFiles();
            }
        };

        const handleDone = async () => {
            if (selectedFiles.length === 0) return;

            setIsImporting(true);
            setImportError(null);
            setImportSuccess(null);

            try {
                // Call the stream upload endpoint
                const experimentId = experiment?.uuid || 'missing-experiment-uuid';
                const result = await importFastqFiles({
                    experimentId: experimentId,
                    fileIds: selectedFiles,
                });

                if (result.success && result.sessionId) {
                    // Clear selection after successful import
                    clearSelection();

                    // Immediately refresh the workflows list to show the new import
                    await refreshExistingWorkflows();
                    setIsImporting(false);
                } else {
                    setImportError(result.message || 'Failed to initiate stream upload');
                    setIsImporting(false);
                }
            } catch (error) {
                setImportError('An unexpected error occurred during import');
                console.error('Import error:', error);
                setIsImporting(false);
            }
        };

        // Listen for specific Synapse stream upload status updates via WebSocket process_synapse_stream_upload
        useWebSocketEvent('send_notification', (data) => {
            webSocketLogger.info('Received Synapse stream upload status update via WebSocket:', data);

            // Ensure experiment is defined and data has necessary properties
            if (!experiment || !data || !data.experiment_uuid || !data.session_id) {
                webSocketLogger.info(
                    'WebSocket update ignored: Missing experiment context or required data fields (experiment_uuid, session_id).',
                );
                return;
            }

            // Check if the update is for the current experiment
            if (data.experiment_uuid === experiment.uuid) {
                webSocketLogger.info(
                    `WebSocket update matches current experiment ${experiment.uuid}. Checking status for session ${data.session_id}`,
                );
                // Trigger a status check for the specific workflow session
                checkWorkflowStatus(experiment.uuid, data.session_id);
            } else {
                webSocketLogger.info('WebSocket update does not match current experiment or is missing data.');
            }
        });

        return (
            <div className="flex flex-col h-full">
                <div className="flex flex-row gap-4 h-full">
                    {/* Left Column - Project Info and FASTQ Files */}
                    <div className="flex flex-col w-3/5">
                        <div>
                            <ShadowCard>
                                <form onSubmit={handleSearch} className="flex flex-col space-y-2 p-3">
                                    <div className="mb-0">
                                        <label
                                            htmlFor="projectId"
                                            className="block text-sm font-medium text-gray-700 mb-1"
                                        >
                                            Synapse project ID
                                        </label>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <input
                                                    id="projectId"
                                                    type="text"
                                                    value={projectId}
                                                    onChange={handleProjectIdChange}
                                                    className="w-1/3 p-2 mr-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                    placeholder="Synapse project ID"
                                                    required
                                                />
                                                <div className="w-3/4">
                                                    <RecentProjectsDropdown
                                                        onSelectProject={(id) => {
                                                            setProjectId(id);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                disabled={!projectId || isSearching}
                                                loading={isSearching}
                                            >
                                                Search
                                            </Button>
                                        </div>
                                    </div>
                                </form>

                                {/* Search Error */}
                                {searchError && <AlertWrapper message={searchError} />}

                                {/* Project Info Card - with persistent height to prevent layout shifts */}
                                {projectInfo && (
                                    <div>
                                        {isLoadingProjectInfo ? (
                                            <div className="p-4 flex items-center">
                                                <CircularProgress size={20} className="mr-2" />
                                                <span>Loading project information...</span>
                                            </div>
                                        ) : (
                                            <div className="p-4">
                                                <h3 className="text-xl font-semibold mb-2">{projectInfo.name}</h3>
                                                {projectInfo.description && (
                                                    <p className="text-sm text-gray-600 mb-2">
                                                        {projectInfo.description}
                                                    </p>
                                                )}
                                                <div className="flex flex-wrap text-xs text-gray-500 gap-x-4">
                                                    <div>
                                                        {projectInfo.id} &bull;{' '}
                                                        {projectInfo.modifiedOn && (
                                                            <span>
                                                                Last modified{' '}
                                                                {new Date(projectInfo.modifiedOn).toLocaleString()}
                                                            </span>
                                                        )}{' '}
                                                        &bull;
                                                        <a
                                                            href={`https://www.synapse.org/#!Synapse:${projectInfo.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="primary-blue hover:underline ml-1"
                                                        >
                                                            View in Synapse{' '}
                                                            <ArrowTopRightOnSquareIcon
                                                                className="ml-1 inline-block"
                                                                width={18}
                                                            />
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Search Progress and Error area - with persistent height to prevent layout shifts */}
                                {isSearching && (
                                    <div className="min-h-[60px] mt-4">
                                        <div className="p-6">
                                            <p className="text-sm mb-2">
                                                Searching for FASTQ files... ({progress.processed} of{' '}
                                                {Math.max(progress.total, 1)} folders processed)
                                            </p>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className="bg-blue-600 h-2.5 rounded-full"
                                                    style={{
                                                        width: `${progress.total ? (progress.processed / progress.total) * 100 : 0}%`,
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* No Results Message */}
                                {searchComplete && fastqFiles.length === 0 && projectInfo && (
                                    <div className="mb-4 p-4 bg-gray-50 rounded-md text-center">
                                        <p className="text-gray-600">No FASTQ files found in this project.</p>
                                    </div>
                                )}

                                {/* FASTQ Files List */}
                                {fastqFiles.length > 0 && !isLoadingProjectInfo && (
                                    <div className="flex-grow flex flex-col">
                                        <div className="flex justify-between items-center p-3 border-b">
                                            <div className="flex items-center space-x-4">
                                                <h3 className="text-lg font-semibold">
                                                    Found {fastqFiles.length} FASTQ files
                                                </h3>
                                            </div>
                                            <div className="flex space-x-4 items-center">
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => toggleSelectAll(fastqFiles.map((file) => file.id))}
                                                >
                                                    {selectedFiles.length === fastqFiles.length
                                                        ? 'Deselect all'
                                                        : 'Select all'}
                                                </Button>
                                                {modalView && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleDone}
                                                        disabled={selectedFiles.length === 0}
                                                        loading={isImporting}
                                                    >
                                                        Import ({selectedFiles.length})
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {(importError || importSuccess) && (
                                            <div className="mx-4 mb-4 min-h-[40px]">
                                                <AlertWrapper
                                                    message={importError || importSuccess}
                                                    severity={importError ? 'error' : 'success'}
                                                />
                                            </div>
                                        )}

                                        <div className="flex-grow overflow-auto h-[calc(100vh-34rem)] min-h-36">
                                            <ul className="divide-y divide-gray-200">
                                                {fastqFiles.map((file) => (
                                                    <li
                                                        key={file.id}
                                                        className={`hover:bg-gray-50 cursor-pointer ${
                                                            selectedFiles.includes(file.id) ? 'bg-blue-50' : ''
                                                        }`}
                                                        onClick={() => toggleFileSelection(file.id)}
                                                    >
                                                        <div className="flex items-center px-4 py-3">
                                                            <div
                                                                className="flex-none w-8 h-8 mr-3 flex items-center justify-center cursor-pointer"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    toggleFileSelection(file.id);
                                                                    return false;
                                                                }}
                                                            >
                                                                <div className="w-5 h-5 border border-gray-400 rounded bg-white flex items-center justify-center">
                                                                    {selectedFiles.includes(file.id) && (
                                                                        <div className="w-3 h-3 bg-indigo-500 rounded-sm"></div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center">
                                                                    <DocumentTextIcon className="w-5 h-5 text-indigo-500 mr-2" />
                                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                                        {file.name}
                                                                    </p>
                                                                </div>
                                                                <p className="text-xs text-gray-500 truncate mt-1">
                                                                    &#8627; {file.path}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </ShadowCard>
                        </div>
                    </div>

                    {/* Right Column - Import Workflows */}
                    <div className="flex flex-col w-2/5">
                        {existingWorkflows.length > 0 ? (
                            <div className="h-full">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-lg font-semibold">Synapse import jobs</h4>

                                    <Tooltip title="Refresh imports" arrow>
                                        <div
                                            className="cursor-pointer text-gray-600 hover:text-blue-600"
                                            onClick={() => {
                                                if (!isLoadingWorkflows) {
                                                    refreshExistingWorkflows();
                                                }
                                            }}
                                        >
                                            <RefreshIcon
                                                className={isLoadingWorkflows ? 'animate-spin opacity-50' : ''}
                                            />
                                        </div>
                                    </Tooltip>
                                </div>
                                <div className="mb-4">
                                    <p>
                                        Any in-progress imports will continue in the background, you will receive an
                                        email notification when the import is complete. It is safe to navigate away from
                                        this page.
                                    </p>
                                </div>
                                {/* Status message area */}
                                {isLoadingWorkflows && (
                                    <div className="mb-4 min-h-[56px]">
                                        <div className="p-3 bg-blue-50 rounded-md flex items-center">
                                            <CircularProgress size={20} className="mr-2" />
                                            <span>Checking for existing import workflows...</span>
                                        </div>
                                    </div>
                                )}
                                <div className="h-[calc(100%-50px)] overflow-auto">
                                    <div className="space-y-4">
                                        {/* Show "I'm done importing files" button when all workflows are complete */}
                                        {existingWorkflows.length > 0 &&
                                            existingWorkflows.every(
                                                (wf) => wf.overallStatus.toLowerCase() === 'succeeded',
                                            ) && (
                                                <div className="mb-4">
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        fullWidth
                                                        onClick={onDoneUploading}
                                                    >
                                                        I&apos;m done importing files
                                                    </Button>
                                                </div>
                                            )}

                                        {existingWorkflows.map((workflow) => {
                                            return (
                                                <div key={workflow.sessionId} className="p-4 bg-gray-50 rounded-md">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center">
                                                            <span className="text-sm font-medium mr-2">Job status</span>
                                                            <div
                                                                className={`inline-block rounded-full border px-2 py-0 text-sm capitalize ${
                                                                    workflow.overallStatus.toLowerCase() === 'succeeded'
                                                                        ? 'border-[#438B50] text-[#438B50]'
                                                                        : workflow.overallStatus.toLowerCase() ===
                                                                            'failed'
                                                                          ? 'border-[#B74B47] text-[#B74B47]'
                                                                          : 'border-[#DF8A35] text-[#DF8A35]'
                                                                } bg-white`}
                                                            >
                                                                {getDisplayStatus(workflow.overallStatus)}
                                                            </div>
                                                            {/* Removed spinner that might trigger updates */}
                                                        </div>
                                                        <div className="flex items-center">
                                                            <div className="text-xs text-gray-500 mr-2">
                                                                {new Date(workflow.createdAt).toLocaleString()}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mt-2">
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="border-b">
                                                                    <th className="text-left py-2 font-semibold">
                                                                        File
                                                                    </th>
                                                                    <th className="text-right py-2 font-semibold">
                                                                        Status
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {workflow.files.map((file, index) => (
                                                                    <tr
                                                                        key={index}
                                                                        className="border-b border-gray-100"
                                                                    >
                                                                        <td
                                                                            className="py-2 truncate"
                                                                            style={{ maxWidth: '200px' }}
                                                                        >
                                                                            {file.fileName}
                                                                        </td>
                                                                        <td className="py-2 text-right">
                                                                            <div
                                                                                className={`inline-block rounded-full border px-2 py-0 text-sm capitalize ${
                                                                                    file.status.toLowerCase() ===
                                                                                    'succeeded'
                                                                                        ? 'border-[#438B50] text-[#438B50]'
                                                                                        : file.status.toLowerCase() ===
                                                                                            'failed'
                                                                                          ? 'border-[#B74B47] text-[#B74B47]'
                                                                                          : 'border-[#DF8A35] text-[#DF8A35]'
                                                                                } bg-white`}
                                                                            >
                                                                                {getDisplayStatus(file.status)}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 bg-gray-50 rounded-md">
                                <p className="text-gray-600">No import jobs yet</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);

export default SynapseFastqViewer;
